/**
 * Tag ordering utility
 *
 * Defines the order in which exercise tags should be displayed in the workout composition.
 * Tags are ordered by their type/category first, then alphabetically within each category.
 */

import { Tag } from '@src/data/exercise-response'
import { ExerciseTag } from '@src/util/default-values'

/**
 * The order in which tag types should be displayed.
 * Lower index = higher priority (displayed first)
 */
export const TAG_TYPE_ORDER = [
  'ExerciseTags::Execution',          // 1. Execution: Standing, Sitting, Lying
  'ExerciseTags::Category',           // 2. Category: Fitness, Rehab, Kids
  'ExerciseTags::BodyArea',           // 3. Body Areas: Core, Lower Body, Upper Body, Total Body
  'ExerciseTags::MuscleGroup',        // 4. Muscle Group: Abs, Back, Legs, Biceps, Calves, Chest, Triceps, Pelvic Floor, Glutes
  'ExerciseTags::Joints',             // 5. Joints: Shoulder, Knees, Hip, C-Spine, Elbow, Wrist, Ankles, Finger
  'ExerciseTags::MovementPattern',    // 6. Movement Pattern: Bend, Hinge, Isolated, Jumping, Lunge, Pull, Push, Rotate, Running, Walking, Squat, Stretching
  'ExerciseTags::RangeOfMotion',      // 7. Range of Motion: Full, 50%, 25%
  'ExerciseTags::Exercise',           // 8. Exercise: Active, Passive, Led
  'ExerciseTags::Focus',              // 9. Focus: Recovery, Balance, Cardio, High Intensity, Isometric hold, Mobilization, Reactive, SAQ, Strength, Strength Endurance
  'ExerciseTags::ClinicalPictures',   // 10. Clinical Pictures: Herniated Disc, Cervical Spine, Thoracic Spine, Lumbar Spine, Knee TEP, Shoulder TEP, Hip TEP
  'ExerciseTags::Level',              // Legacy: Level/Phase
  'ExerciseTags::HealthLimitation',   // Legacy: Health Limitations
  'ExerciseTags::ContentType'         // Legacy: Content Type
]

/**
 * Custom order for specific tags within each category.
 * This allows fine-grained control over the display order of individual tags.
 */
export const TAG_KEY_ORDER: { [key: string]: string[] } = {
  'ExerciseTags::Execution': [
    'standing',
    'sitting',
    'lying'
  ],
  'ExerciseTags::Category': [
    'fitness',
    'rehab',
    'kids'
  ],
  'ExerciseTags::BodyArea': [
    'core',
    'lower_body',
    'upper_body',
    'total_body'
  ],
  'ExerciseTags::MuscleGroup': [
    'abs',
    'back',
    'legs',
    'biceps',
    'calves',
    'chest',
    'triceps',
    'pelvic_floor',
    'glutes'
  ],
  'ExerciseTags::Joints': [
    'shoulder',
    'knees',
    'hip',
    'c_spine',
    'elbow',
    'wrist',
    'ankles',
    'finger'
  ],
  'ExerciseTags::MovementPattern': [
    'bend',
    'hinge',
    'isolated',
    'jumping',
    'lunge',
    'pull',
    'push',
    'rotate',
    'rotate_twist',
    'running',
    'walking',
    'squat',
    'stretching'
  ],
  'ExerciseTags::RangeOfMotion': [
    'full',
    '50%',
    '25%'
  ],
  'ExerciseTags::Exercise': [
    'active',
    'passive',
    'led'
  ],
  'ExerciseTags::Focus': [
    'recovery',
    'balance',
    'cardio',
    'high_intensity',
    'isometric_hold',
    'mobilisation',
    'reactive',
    'saq',
    'strength',
    'strength_endurance'
  ],
  'ExerciseTags::ClinicalPictures': [
    'herniated_disc',
    'cervical_spine',
    'c_spine',
    'thoracic_spine',
    'tsp',
    'lumbar_spine',
    'lws',
    'knee_tep',
    'shoulder_tep',
    'hip_tep'
  ]
}

/**
 * Get the priority index for a tag type.
 * Lower index = higher priority.
 *
 * @param tagType - The type of the tag (e.g., 'ExerciseTags::MovementPattern')
 * @returns The priority index (lower is higher priority)
 */
export function getTagTypePriority (tagType: string): number {
  const index = TAG_TYPE_ORDER.indexOf(tagType)
  return index === -1 ? TAG_TYPE_ORDER.length : index
}

/**
 * Get the priority index for a tag key within its category.
 * Lower index = higher priority.
 *
 * @param tagType - The type of the tag (e.g., 'ExerciseTags::MovementPattern')
 * @param tagKey - The key of the tag (e.g., 'squat')
 * @returns The priority index (lower is higher priority)
 */
export function getTagKeyPriority (tagType: string, tagKey: string): number {
  const order = TAG_KEY_ORDER[tagType]
  if (!order) return -1

  const normalizedKey = tagKey.toLowerCase().replace('/', '_')
  const index = order.indexOf(normalizedKey)
  return index === -1 ? order.length : index
}

/**
 * Sort exercise tags by their category priority and then by their key priority.
 *
 * @param tags - Array of tags to sort
 * @returns Sorted array of tags
 */
export function sortExerciseTags (tags: Tag[]): Tag[] {
  return [...tags].sort((a, b) => {
    // First, sort by tag type priority
    const typePriorityA = getTagTypePriority(a.type)
    const typePriorityB = getTagTypePriority(b.type)

    if (typePriorityA !== typePriorityB) {
      return typePriorityA - typePriorityB
    }

    // If same type, sort by key priority within that type
    const keyPriorityA = getTagKeyPriority(a.type, a.key)
    const keyPriorityB = getTagKeyPriority(b.type, b.key)

    if (keyPriorityA !== -1 || keyPriorityB !== -1) {
      // If we have custom ordering for this type
      // istanbul ignore if: both tags have the same type here, so both or neither have a custom order
      if (keyPriorityA === -1) return 1  // a is not in custom order, put it after
      // istanbul ignore if: see above
      if (keyPriorityB === -1) return -1 // b is not in custom order, put it after
      if (keyPriorityA !== keyPriorityB) {
        return keyPriorityA - keyPriorityB
      }
    }

    // If no custom ordering or same priority, sort alphabetically
    return a.key.localeCompare(b.key)
  })
}

/**
 * Sort tag keys by their category and priority.
 * Used when you only have tag keys without their types.
 *
 * @param tagKeys - Array of tag keys to sort
 * @param exerciseTags - Complete list of exercise tags with type information
 * @returns Sorted array of tag keys
 */
export function sortTagKeys (tagKeys: string[], exerciseTags: ExerciseTag[]): string[] {
  // Create a map of tag key to tag type
  const tagKeyToType = new Map<string, string>()
  exerciseTags.forEach(tag => {
    const normalizedKey = tag.key.toLowerCase().replace('/', '_')
    tagKeyToType.set(normalizedKey, tag.type)
  })

  return [...tagKeys].sort((a, b) => {
    const typeA = tagKeyToType.get(a.toLowerCase())
    const typeB = tagKeyToType.get(b.toLowerCase())

    if (!typeA || !typeB) {
      // If we can't find the type, fall back to alphabetical
      return a.localeCompare(b)
    }

    // Sort by type priority
    const typePriorityA = getTagTypePriority(typeA)
    const typePriorityB = getTagTypePriority(typeB)

    if (typePriorityA !== typePriorityB) {
      return typePriorityA - typePriorityB
    }

    // If same type, sort by key priority within that type
    const keyPriorityA = getTagKeyPriority(typeA, a)
    const keyPriorityB = getTagKeyPriority(typeB, b)

    if (keyPriorityA !== -1 || keyPriorityB !== -1) {
      if (keyPriorityA === -1) return 1
      if (keyPriorityB === -1) return -1
      if (keyPriorityA !== keyPriorityB) {
        return keyPriorityA - keyPriorityB
      }
    }

    // Fall back to alphabetical
    return a.localeCompare(b)
  })
}

/**
 * Sort the exerciseTags object by maintaining the category order.
 *
 * @param exerciseTags - The exerciseTags object with categories as keys
 * @returns Sorted exerciseTags object
 */
export function sortExerciseTagsByCategory (exerciseTags: { [key: string]: string[] }): { [key: string]: string[] } {
  const sorted: { [key: string]: string[] } = {}

  // First, add categories in the defined order
  TAG_TYPE_ORDER.forEach(tagType => {
    if (exerciseTags[tagType]) {
      const order = TAG_KEY_ORDER[tagType]
      if (order) {
        // Sort by custom order if available
        sorted[tagType] = [...exerciseTags[tagType]].sort((a, b) => {
          const indexA = order.indexOf(a.toLowerCase().replace('/', '_'))
          const indexB = order.indexOf(b.toLowerCase().replace('/', '_'))

          if (indexA === -1 && indexB === -1) return a.localeCompare(b)
          if (indexA === -1) return 1
          if (indexB === -1) return -1
          return indexA - indexB
        })
      } else {
        // Sort alphabetically if no custom order
        sorted[tagType] = [...exerciseTags[tagType]].sort((a, b) => a.localeCompare(b))
      }
    }
  })

  // Then add any categories not in the defined order (for backwards compatibility)
  Object.keys(exerciseTags).forEach(tagType => {
    if (!sorted[tagType]) {
      sorted[tagType] = [...exerciseTags[tagType]].sort((a, b) => a.localeCompare(b))
    }
  })

  return sorted
}
